/**
 * StockEntryItemsSection — Shopee/Lazada-style product-card list for the
 * Stock Entry child table. Replaces the raw child-table grid with a row
 * per item showing: image placeholder, item_code + item_name, warehouse
 * flow, qty × uom, and line amount.
 *
 * Read-only display in this slice. Full editing lives in the "Additional
 * Details" tab which uses GenericSection with the child-table modal.
 *
 * Design language matches the sibling Overview card:
 *   - CARD token from design-tokens.ts
 *   - uppercase pl-4 section header
 *   - row cards with rounded-xl, border-border/40 on light background
 */

import { ArrowRight, Package } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/i18n/useLanguage";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

const i18n: Record<string, { en: string; th: string }> = {
  items: { en: "Items", th: "สินค้า" },
  items_count: { en: "items", th: "รายการ" },
  no_items: { en: "No items added", th: "ยังไม่มีสินค้า" },
  total_qty: { en: "Total qty", th: "จำนวนรวม" },
  units: { en: "units", th: "หน่วย" },
};

interface StockEntryDetailRow {
  item_code?: string;
  item_name?: string;
  qty?: number;
  uom?: string;
  stock_uom?: string;
  s_warehouse?: string;
  t_warehouse?: string;
  basic_rate?: number;
  basic_amount?: number;
  amount?: number;
  image?: string;
  idx?: number;
}

function formatCurrency(value: number | undefined, currency: string): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "THB",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || "THB"}`;
  }
}

function formatQty(qty: number | undefined): string {
  if (qty === undefined || qty === null || Number.isNaN(qty)) return "0";
  // Integer display when whole number, else up to 3 decimals
  return Number.isInteger(qty) ? String(qty) : qty.toFixed(3).replace(/\.?0+$/, "");
}

function shortenWarehouse(wh: string): string {
  // Strip company suffix "Stores - D6EA" → "Stores"
  const idx = wh.lastIndexOf(" - ");
  return idx > 0 ? wh.slice(0, idx) : wh;
}

export function StockEntryItemsSection({ formData }: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const items: StockEntryDetailRow[] = Array.isArray(formData.items) ? formData.items : [];
  const currency = (formData.currency as string) || "THB";

  const totalQty = items.reduce((acc, r) => acc + (Number(r.qty) || 0), 0);
  const totalAmount = items.reduce(
    (acc, r) => acc + (Number(r.basic_amount ?? r.amount) || 0),
    0,
  );

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between pl-4 pr-1">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {t("items")}
        </h3>
        {items.length > 0 ? (
          <span className="text-xs text-muted-foreground">
            {items.length} {t("items_count")}
          </span>
        ) : null}
      </div>
      <Card className={CARD}>
        <CardContent className="p-5 space-y-3">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-muted mb-3">
                <Package className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-sm font-medium text-muted-foreground">
                {t("no_items")}
              </div>
            </div>
          ) : (
            <>
              {items.map((row, idx) => {
                const sourceWh = row.s_warehouse ? shortenWarehouse(row.s_warehouse) : "";
                const targetWh = row.t_warehouse ? shortenWarehouse(row.t_warehouse) : "";
                const lineAmount = row.basic_amount ?? row.amount;
                const uom = row.uom || row.stock_uom || "";
                return (
                  <div
                    key={`${row.item_code ?? "row"}-${row.idx ?? idx}`}
                    className="flex items-center gap-3 rounded-xl border border-border/40 bg-background p-3"
                  >
                    {/* Image placeholder */}
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted shrink-0 overflow-hidden">
                      {row.image ? (
                        <img
                          src={row.image}
                          alt={row.item_name ?? row.item_code ?? ""}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>

                    {/* Item info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {row.item_code || "—"}
                      </div>
                      {row.item_name && row.item_name !== row.item_code ? (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">
                          {row.item_name}
                        </div>
                      ) : null}
                      {(sourceWh || targetWh) ? (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground min-w-0">
                          {sourceWh ? (
                            <span className="truncate">{sourceWh}</span>
                          ) : null}
                          {sourceWh && targetWh ? (
                            <ArrowRight className="h-3 w-3 shrink-0" />
                          ) : null}
                          {targetWh ? (
                            <span className="truncate">{targetWh}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    {/* Qty + amount */}
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-foreground">
                        {formatQty(row.qty)} {uom}
                      </div>
                      {lineAmount !== undefined && lineAmount !== null ? (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {formatCurrency(lineAmount, currency)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}

              {/* Totals footer */}
              <div className="flex items-center justify-between pt-3 border-t border-border/40">
                <span className="text-xs text-muted-foreground">
                  {t("total_qty")}
                </span>
                <div className="text-right">
                  <div className="text-sm font-semibold text-foreground">
                    {formatQty(totalQty)} {t("units")}
                  </div>
                  {totalAmount > 0 ? (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatCurrency(totalAmount, currency)}
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
